import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';

export const EDITOR_MODES = ['generate', 'apply', 'verify'] as const;

export type EditorMode = (typeof EDITOR_MODES)[number];

interface ModeDefinition {
  value: EditorMode;
  label: string;
  icon: React.ReactNode;
  hint: string;
}

const MODES: ModeDefinition[] = [
  {
    value: 'generate',
    label: 'Generate',
    icon: <AutoFixHighIcon sx={{ fontSize: 18 }} />,
    hint: 'Compare two documents and create the RFC 6902 patch between them.'
  },
  {
    value: 'apply',
    label: 'Apply',
    icon: <PlayArrowIcon sx={{ fontSize: 18 }} />,
    hint: 'Have a patch already? Paste it next to your source document and run it.'
  },
  {
    value: 'verify',
    label: 'Verify',
    icon: <VerifiedIcon sx={{ fontSize: 18 }} />,
    hint: 'Confirm a patch transforms Source into Target exactly - nothing more, nothing less.'
  }
];

// Apple-style ease-out, shared by the pill indicator and the view slides
export const MODE_TRANSITION_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const MODE_TRANSITION_MS = 320;

interface ModeTabsProps {
  mode: EditorMode;
  onChange: (mode: EditorMode) => void;
}

const ModeTabs = ({ mode, onChange }: ModeTabsProps) => {
  const activeIndex = EDITOR_MODES.indexOf(mode);
  const activeHint = MODES.find((m) => m.value === mode)?.hint;
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  // APG tabs pattern with automatic activation: arrow keys move selection
  // and focus together, Home/End jump to the edges.
  const focusMode = (index: number) => {
    onChange(EDITOR_MODES[index]);
    requestAnimationFrame(() => tabRefs.current[index]?.focus());
  };

  const handleTablistKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusMode((activeIndex + 1) % EDITOR_MODES.length);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusMode((activeIndex - 1 + EDITOR_MODES.length) % EDITOR_MODES.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusMode(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusMode(EDITOR_MODES.length - 1);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, mb: 3.5 }}>
      <Box
        role="tablist"
        aria-label="Workflow mode"
        onKeyDown={handleTablistKeyDown}
        sx={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          maxWidth: 420,
          p: '5px',
          backgroundColor: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '9999px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}
      >
        {/* Sliding indicator capsule - glides between equal-width segments */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 5,
            bottom: 5,
            left: 5,
            width: `calc((100% - 10px) / ${EDITOR_MODES.length})`,
            borderRadius: '9999px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
            transform: `translateX(${activeIndex * 100}%)`,
            transition: `transform ${MODE_TRANSITION_MS}ms ${MODE_TRANSITION_EASE}`
          }}
        />
        {MODES.map(({ value, label, icon }) => {
          const isActive = mode === value;
          return (
            <Button
              key={value}
              role="tab"
              aria-selected={isActive}
              ref={(el: HTMLButtonElement | null) => {
                tabRefs.current[EDITOR_MODES.indexOf(value)] = el;
              }}
              startIcon={icon}
              onClick={() => onChange(value)}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                position: 'relative',
                zIndex: 1,
                borderRadius: '9999px',
                px: { xs: 1, sm: 2 },
                py: 1.05,
                fontSize: { xs: '0.85rem', sm: '0.925rem' },
                fontWeight: 600,
                textTransform: 'none',
                color: isActive ? '#fff' : 'var(--fg-muted)',
                background: 'transparent',
                boxShadow: 'none',
                transition: `color ${MODE_TRANSITION_MS}ms ${MODE_TRANSITION_EASE}`,
                '&:hover': {
                  color: isActive ? '#fff' : 'var(--fg)',
                  background: isActive ? 'transparent' : 'var(--surface-hover)',
                  boxShadow: 'none'
                }
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>
      <Typography variant="body2" sx={{ color: 'var(--fg-muted)', fontSize: '0.85rem', textAlign: 'center', minHeight: '1.4em', px: 2 }}>
        {activeHint}
      </Typography>
    </Box>
  );
};

export default ModeTabs;
